import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DepartmentNav } from '../components/DepartmentNav';
import { UpgradeGate } from '../components/UpgradeGate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Envelope, Bell, BellRinging, CheckCircle, Wind, CalendarCheck, Bug, Leaf } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

export const EmailPreferencesPage = () => {
  const { t } = useTranslation(['common', 'nav', 'profile']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    gdd_alerts_enabled: true,
    alert_threshold_percent: 90,
    weekly_summary: false,
    spray_alerts_enabled: true,
    spray_alert_threshold: 'good',
    monthly_pest_alerts: true,
    pest_alert_day: 1
  });
  const [testEmailSending, setTestEmailSending] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/email-preferences`, { withCredentials: true });
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch email preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/email-preferences`, preferences, { withCredentials: true });
      toast.success('Email preferences saved!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async (type) => {
    setTestEmailSending(true);
    try {
      const endpoints = {
        'welcome': '/api/test/send-welcome-email',
        'gdd': '/api/test/send-gdd-alert-email',
        'spray': '/api/test/send-spray-alert-email',
        'renewal': '/api/test/send-renewal-reminder-email',
        'pest': '/api/test/send-pest-report-email'
      };
      const endpoint = endpoints[type];
      const response = await axios.post(`${API_URL}${endpoint}`, {}, { withCredentials: true });
      if (response.data.status === 'success') {
        toast.success('Test email sent! Check your inbox.');
      } else if (response.data.status === 'skipped') {
        toast.info('Email skipped: ' + response.data.reason);
      } else {
        toast.error('Failed to send test email');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestEmailSending(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="email-preferences-page" className="min-h-screen bg-background">
        <DepartmentNav />
        <UpgradeGate feature="email_alerts" featureLabel="Email Alerts & Notifications">
        <div className="flex items-center justify-center min-h-[300px]">
          <p className="text-muted-foreground">Loading preferences...</p>
        </div>
        </UpgradeGate>
      </div>
    );
  }

  return (
    <div data-testid="email-preferences-page" className="min-h-screen bg-background">
      <DepartmentNav />
      <UpgradeGate feature="email_alerts" featureLabel="Email Alerts & Notifications">
      <main id="main-content" className="max-w-3xl mx-auto p-6 space-y-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Email Alerts</h1>
          <p className="text-muted-foreground mt-1">Manage your GDD notifications and email preferences</p>
        </div>
      <Card data-testid="email-preferences-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Envelope weight="duotone" size={24} className="text-primary" aria-hidden="true" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Control how and when TurfPulse sends you email notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* GDD Alerts Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="gdd-alerts" className="flex items-center gap-2">
                <BellRinging weight="duotone" size={20} className="text-primary" aria-hidden="true" />
                GDD Threshold Alerts
              </Label>
              <p className="text-sm text-muted-foreground">
                Get notified when your GDD approaches the application threshold
              </p>
            </div>
            <Switch
              id="gdd-alerts"
              data-testid="gdd-alerts-toggle"
              checked={preferences.gdd_alerts_enabled}
              onCheckedChange={(checked) => setPreferences({ ...preferences, gdd_alerts_enabled: checked })}
            />
          </div>

          {/* Alert Threshold */}
          {preferences.gdd_alerts_enabled && (
            <div className="pl-8 border-l-2 border-primary/20 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="threshold-percent">Alert me when GDD reaches</Label>
                <Select
                  value={String(preferences.alert_threshold_percent)}
                  onValueChange={(value) => setPreferences({ ...preferences, alert_threshold_percent: parseInt(value) })}
                >
                  <SelectTrigger className="w-[180px]" data-testid="threshold-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="70">70% of threshold</SelectItem>
                    <SelectItem value="80">80% of threshold</SelectItem>
                    <SelectItem value="90">90% of threshold</SelectItem>
                    <SelectItem value="95">95% of threshold</SelectItem>
                    <SelectItem value="100">100% (at threshold)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  You'll receive an alert when your accumulated GDD reaches this percentage of your target threshold
                </p>
              </div>
            </div>
          )}

          {/* Spray Condition Alerts */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="space-y-0.5">
              <Label htmlFor="spray-alerts" className="flex items-center gap-2">
                <Wind weight="duotone" size={20} className="text-blue-500" aria-hidden="true" />
                Spray Condition Alerts
              </Label>
              <p className="text-sm text-muted-foreground">
                Get notified when weather conditions are ideal for spray applications
              </p>
            </div>
            <Switch
              id="spray-alerts"
              data-testid="spray-alerts-toggle"
              checked={preferences.spray_alerts_enabled}
              onCheckedChange={(checked) => setPreferences({ ...preferences, spray_alerts_enabled: checked })}
            />
          </div>

          {/* Spray Alert Threshold */}
          {preferences.spray_alerts_enabled && (
            <div className="pl-8 border-l-2 border-blue-500/20 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="spray-threshold">Alert me when conditions are</Label>
                <Select
                  value={preferences.spray_alert_threshold}
                  onValueChange={(value) => setPreferences({ ...preferences, spray_alert_threshold: value })}
                >
                  <SelectTrigger className="w-[200px]" data-testid="spray-threshold-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ideal">Ideal (0-3 mph wind)</SelectItem>
                    <SelectItem value="good">Good or better (0-5 mph)</SelectItem>
                    <SelectItem value="caution">Caution or better (0-10 mph)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  You'll be notified when wind conditions meet this threshold in the 5-day forecast
                </p>
              </div>
            </div>
          )}

          {/* Monthly Pest/Weed GDD Alerts */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="space-y-0.5">
              <Label htmlFor="monthly-pest-alerts" className="flex items-center gap-2">
                <Bug weight="duotone" size={20} className="text-amber-600" aria-hidden="true" />
                Monthly Pest & Weed Report
              </Label>
              <p className="text-sm text-muted-foreground">
                Get a monthly report showing what pests/weeds are active based on your GDD accumulation
              </p>
            </div>
            <Switch
              id="monthly-pest-alerts"
              data-testid="monthly-pest-alerts-toggle"
              checked={preferences.monthly_pest_alerts}
              onCheckedChange={(checked) => setPreferences({ ...preferences, monthly_pest_alerts: checked })}
            />
          </div>

          {/* Monthly Alert Day Selection */}
          {preferences.monthly_pest_alerts && (
            <div className="pl-8 border-l-2 border-amber-500/20 space-y-3">
              <div className="space-y-2">
                <Label htmlFor="pest-alert-day">Send report on day</Label>
                <Select
                  value={String(preferences.pest_alert_day)}
                  onValueChange={(value) => setPreferences({ ...preferences, pest_alert_day: parseInt(value) })}
                >
                  <SelectTrigger className="w-[200px]" data-testid="pest-alert-day-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1st of the month</SelectItem>
                    <SelectItem value="15">15th of the month</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Report includes: active pests/weeds based on your YTD GDD, recommended products, and what to watch for next month
                </p>
              </div>
              
              {/* Preview of what's included */}
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-2">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-1">
                  <Leaf size={14} /> What's included in your monthly report:
                </p>
                <ul className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                  <li>• Current YTD GDD for your location (base 32°F & 50°F)</li>
                  <li>• Active pests & weeds based on GDD thresholds</li>
                  <li>• Recommended post-emergent products</li>
                  <li>• What's emerging next month to prepare for</li>
                  <li>• Pro tips for your grass type</li>
                </ul>
              </div>
            </div>
          )}

          {/* Weekly Summary */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="space-y-0.5">
              <Label htmlFor="weekly-summary" className="flex items-center gap-2">
                <Bell weight="duotone" size={20} className="text-muted-foreground" aria-hidden="true" />
                Weekly Summary
              </Label>
              <p className="text-sm text-muted-foreground">
                Receive a weekly email with your lawn care stats and recommendations
              </p>
            </div>
            <Switch
              id="weekly-summary"
              data-testid="weekly-summary-toggle"
              checked={preferences.weekly_summary}
              onCheckedChange={(checked) => setPreferences({ ...preferences, weekly_summary: checked })}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={savePreferences} 
              disabled={saving}
              data-testid="save-preferences-btn"
            >
              {saving ? 'Saving...' : 'Save Preferences'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Emails Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Test Emails</CardTitle>
          <CardDescription>
            Send test emails to verify your email delivery is working
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => sendTestEmail('welcome')}
              disabled={testEmailSending}
              data-testid="test-welcome-email-btn"
            >
              <CheckCircle weight="bold" size={16} className="mr-2" aria-hidden="true" />
              Send Test Welcome Email
            </Button>
            <Button
              variant="outline"
              onClick={() => sendTestEmail('gdd')}
              disabled={testEmailSending}
              data-testid="test-gdd-email-btn"
            >
              <BellRinging weight="bold" size={16} className="mr-2" aria-hidden="true" />
              Send Test GDD Alert
            </Button>
            <Button
              variant="outline"
              onClick={() => sendTestEmail('spray')}
              disabled={testEmailSending}
              data-testid="test-spray-email-btn"
            >
              <Wind weight="bold" size={16} className="mr-2" aria-hidden="true" />
              Send Test Spray Alert
            </Button>
            <Button
              variant="outline"
              onClick={() => sendTestEmail('pest')}
              disabled={testEmailSending}
              data-testid="test-pest-email-btn"
            >
              <Bug weight="bold" size={16} className="mr-2" aria-hidden="true" />
              Send Test Pest Report
            </Button>
            <Button
              variant="outline"
              onClick={() => sendTestEmail('renewal')}
              disabled={testEmailSending}
              data-testid="test-renewal-email-btn"
            >
              <CalendarCheck weight="bold" size={16} className="mr-2" aria-hidden="true" />
              Send Test Renewal Reminder
            </Button>
          </div>
        </CardContent>
      </Card>
      </main>
      </UpgradeGate>
    </div>
  );
};
